import React, { useState, useCallback, useRef, useMemo } from 'react';
import PolarisIcon from '../../../_common/PolarisIcon';
import styled from '@emotion/styled';
import { Button, Typography } from '@mui/material';
import FileType from '@/components/_common/Icons/FileType';
import { setBulkUploadedFile } from '@/redux/slices/bulkSearchSlice';
import { useFile } from '@/contextAPI/fileContext';
import { useFormikContext } from 'formik';
import { Order } from '@/redux/slices/slice_types';
import { useAppDispatch } from '@/redux/hooks';
import { formatBytes } from '@/_utils/utils';

export const BulkFileDisplay = ({ file, handleClearFile }: { file: any; handleClearFile: () => void }) => {
  return (
    <FileDisplay>
      <div>
        <FileType type="xls" />
        <div>
          <Typography variant="h6">{file?.name}</Typography>
          {file.size && (
            <Typography variant="body1" style={{ opacity: 0.7 }}>
              {formatBytes(file?.size)}
            </Typography>
          )}
        </div>
        <CompleteIcon icon="complete" height={23} width={23} />
      </div>
      <div>
        {!!file && (
          <>
            <Button color="default" size="small" variant="text" startIcon={<PolarisIcon icon="close" />} onClick={handleClearFile}>
              clear
            </Button>
          </>
        )}
      </div>
    </FileDisplay>
  );
};

const BulkFileUpload = () => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const { values } = useFormikContext();
  const { file, setFile } = useFile();

  const inputFormat = useMemo(() => {
    const job = (values as Order)?.jobs[0] ?? {};
    const input_format = job?.profile?.data?.template_inputs?.input_format;

    return input_format || 'CSV';
  }, [values]);

  const handleFileUpload = async (e: any) => {
    const uploadedFile = e.target?.files?.[0] || e.dataTransfer.files[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      dispatch(setBulkUploadedFile({ name: uploadedFile.name }));
    }
  };

  const onFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileUpload(e);
  }, []);

  const onFileDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  }, []);

  const onFileClick = useCallback((e: any) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  }, []);

  const handleClearFile = () => {
    setFile(null);
    dispatch(setBulkUploadedFile(null));
  };

  return (
    <div>
      {!file?.name ? (
        <FileInputPseudoButton
          onDragOver={onFileDragOver}
          onDragLeave={() => setIsDraggingOver(false)}
          onDrop={onFileDrop}
          onClick={onFileClick}
          hasFile={file ? 1 : 0}
          isDraggingOver={+isDraggingOver}
        >
          <Typography variant="h6" sx={{ lineHeight: 1.25 }}>
            {isDraggingOver ? (
              'Drop file here'
            ) : (
              <>
                Drop a file here
                <br />
                or click to upload
              </>
            )}
          </Typography>
          <Typography sx={{ opacity: 0.7, mt: -1 }} variant="body1">
            The maximum number of rows that can be processed is 100,000.
          </Typography>

          <StyledImportButton sx={{ height: 40 }} variant="outlined" startIcon={<PolarisIcon icon="import" />}>
            <FileInput
              ref={fileInputRef}
              type="file"
              name="file"
              accept={inputFormat === 'EXCEL' ? '.xlsx' : '.csv'}
              onChange={handleFileUpload}
              onClick={(e) => e.stopPropagation()}
            />
            Import
          </StyledImportButton>
        </FileInputPseudoButton>
      ) : (
        <BulkFileDisplay file={file} handleClearFile={handleClearFile} />
      )}
    </div>
  );
};

export default BulkFileUpload;

const FileInputPseudoButton = styled.div<{ hasFile: number; isDraggingOver: number }>`
  display: flex;
  text-align: center;
  height: 300px;
  border-color: ${({ theme, isDraggingOver }) => (isDraggingOver ? theme.palette.primary.main : theme.palette.default.main)};
  border-width: 1px;
  border-style: dashed;
  background-color: ${({ theme, isDraggingOver }) => (isDraggingOver ? theme.palette.primary.main + '3a' : theme.palette.default.main + '1a')};
  border-radius: 4px;
  color: ${({ theme }) => theme.palette.default.main};
  cursor: ${({ hasFile }) => (hasFile ? 'default' : 'pointer')};
  padding: ${({ theme }) => theme.spacing(3, 5)};
  margin-top: 10px;
  position: relative;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing(2)};
`;

const StyledImportButton = styled(Button)`
  color: ${({ theme }) => theme.palette.primary.main};
  border-width: 1.5px;
`;

const FileInput = styled.input`
  display: none;
`;

const FileDisplay = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.palette.default.main + '1a'};
  padding: ${({ theme }) => theme.spacing(2, 3)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border: 1px solid ${({ theme }) => theme.palette.primary.main + '8a'};

  > div {
    display: flex;
    flex-direction: row;
    column-gap: ${({ theme }) => theme.spacing(2)};
    align-items: center;
  }
`;

const CompleteIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.success.main};
`;
